import { Bullet, Circle, Label, p50, percent } from "@amcharts/amcharts5";
import {
  AxisRenderer,
  AxisRendererX,
  AxisRendererY,
  CategoryAxis,
  ColumnSeries,
  ValueAxis,
  XYChart,
  XYCursor,
} from "@amcharts/amcharts5/xy";
import { cloneDeep, forIn } from "lodash";

import BaseChart, { LegendData } from "@/classes/BaseChart";
import { mapDataDumbbell } from "@/helpers/dumbbellHelpers";
import { axisTitle } from "@/settings/charts";
import { DEFAULT_DATA_LABEL_SIZE } from "@/settings/charts/settingsDictionary";
import { chartFontSettings, chartInitialSettings } from "@/settings/widgets";
import { ChartDataRecord, TooltipSeries } from "@/types/charts";

export default class DumbbellPlotHorizontal extends BaseChart {
  xAxis!: ValueAxis<AxisRenderer>;
  yAxis!: CategoryAxis<AxisRenderer>;
  maxField!: string;
  minField!: string;
  medianField!: string;
  min!: null | number;
  max!: null | number;

  groups: string[] = [];

  initChart() {
    this.root.container.children.clear();

    if (this.chart) {
      (this.chart as XYChart).series.clear();
    }

    this.chart = this.root.container.children.push(
      XYChart.new(this.root, {
        ...chartInitialSettings,
        paddingTop: 0,
        panX: true,
        panY: true,
        wheelX: "panX",
        wheelY: "zoomY",
        layout: this.layout,
      })
    );

    this.createAxes();

    const legendNames: string[] = [];

    this.data.forEach((el: ChartDataRecord, index: number) => {
      const paletteLength = this.palette.length;
      const indexColor = index % paletteLength;
      legendNames.push(el[this.settings.decompositionMetric] as string);
      this.createSeries(el, indexColor);
    });

    this.showLegend(legendNames, (this.chart as XYChart).series.values as LegendData);

    this.createCursor();
  }

  protected createSeries(item: ChartDataRecord, index: number) {
    const series = (this.chart as XYChart).series.push(
      ColumnSeries.new(this.root, {
        fill: this.getSeriesColor(index),
        stroke: this.getSeriesColor(index),
        name: item[this.settings.decompositionMetric] as string,
        xAxis: this.xAxis,
        yAxis: this.yAxis,
        valueXField: this.maxField,
        openValueXField: this.minField,
        categoryYField: this.settings.decompositionMetric,
        clustered: false,
        tooltipPositionX: "low",
        lowValueXField: this.medianField,
      })
    );

    series.columns.template.setAll({ height: 1 });

    series.bullets.push(() =>
      Bullet.new(this.root, {
        locationX: 0,
        sprite: Circle.new(this.root, { radius: 5, fill: series.get("fill") }),
      })
    );

    series.bullets.push(() =>
      Bullet.new(this.root, {
        locationX: 1,
        sprite: Circle.new(this.root, { radius: 5, fill: series.get("fill") }),
      })
    );

    const medianPosition =
      item[this.minField] !== item[this.maxField]
        ? ((item[this.medianField] as number) - (item[this.minField] as number)) /
          ((item[this.maxField] as number) - (item[this.minField] as number))
        : 1;

    series.bullets.push(() =>
      Bullet.new(this.root, {
        locationX: medianPosition,
        sprite: Circle.new(this.root, { radius: 8, fill: series.get("fill") }),
      })
    );

    if (this.settings.showDataLabels) {
      const metricKey = this.settings.displayMetric;
      const metricFormatter = this.formatters?.[metricKey];
      const formatterConfig = this.getDataLabelsFormatterConfig();
      const labelFontSize = Number(this.settings.dataLabelsSizes ?? DEFAULT_DATA_LABEL_SIZE);

      const axis = this.xAxis as ValueAxis<AxisRenderer>;
      const currentExtraMax = Number(axis?.get("extraMax") ?? 0);
      const requiredExtraMax = 0.1;
      if (axis && currentExtraMax < requiredExtraMax) {
        axis.set("extraMax", requiredExtraMax);
      }

      series.bullets.push(() => {
        const label = Label.new(this.root, {
          text: "",
          populateText: false,
          centerY: p50,
          dx: -26,
          dy: -16,
          fontSize: labelFontSize,
        });

        label.adapters.add("text", () => {
          const medianValue = item[this.medianField] as number | undefined;
          if (typeof medianValue !== "number" || !Number.isFinite(medianValue)) {
            return "";
          }
          return metricFormatter
            ? metricFormatter(medianValue, formatterConfig)
            : this.root.numberFormatter.format(medianValue, this.getMetricFormatter(metricKey, formatterConfig));
        });

        label.adapters.add("visible", () => {
          const medianValue = item[this.medianField];
          return typeof medianValue === "number" && Number.isFinite(medianValue);
        });

        return Bullet.new(this.root, {
          locationX: medianPosition,
          sprite: label,
        });
      });
    }

    series.data.push(item);

    this.createTooltip(series as TooltipSeries, () => {
      const param: Record<string, unknown>[] = [];
      const tooltipValues = series.get("tooltip")?.dataItem?.dataContext as Record<string, unknown>;
      if (tooltipValues) {
        forIn(tooltipValues, (value, key) => {
          const shown = this.formatMetricValueForDisplay(key, value);
          param.push({ key, value: shown });
        });
      }
      return param;
    });
  }

  protected createAxes() {
    // We need shifts on the edges because if center of the bullet stands on the edge, we don't see the rest of bullet.
    // So, the shift adds empty space for rest of bullet.
    const shiftOnTheLeftEdge = ((this.max ?? 0) - (this.min ?? 0)) / 20;
    const shiftOnTheRightEdge = ((this.max ?? 0) - (this.min ?? 0)) / 10;
    const min = (this.min ?? 0) - shiftOnTheLeftEdge;
    const max = (this.max ?? 0) + shiftOnTheRightEdge;

    const xRenderer = AxisRendererX.new(this.root, {});
    xRenderer.labels.template.setAll({
      ...chartFontSettings.label,
    });

    const settings = {
      renderer: xRenderer,
      strictMinMax: true,
      min,
      max,
      numberFormat: this.getCompactAxesMetricFormatter(this.settings.displayMetric, this.data),
    };

    this.xAxis = (this.chart as XYChart).xAxes.push(ValueAxis.new(this.root, settings));

    const yRenderer = AxisRendererY.new(this.root, {});
    yRenderer.labels.template.setAll({
      ...chartFontSettings.label,
    });

    this.yAxis = (this.chart as XYChart).yAxes.push(
      CategoryAxis.new(this.root, {
        categoryField: this.settings.decompositionMetric,
        renderer: yRenderer,
      })
    );

    if (this.settings.xAxisTitle) {
      this.xAxis.children.push(
        Label.new(this.root, {
          text: this.settings.xAxisTitle,
          ...chartFontSettings.axisTitle,
          x: p50,
          marginTop: axisTitle.marginTop,
          centerX: p50,
        })
      );
    }

    if (this.settings.yAxisTitle) {
      this.yAxis.children.push(
        Label.new(this.root, {
          text: this.settings.yAxisTitle,
          ...chartFontSettings.axisTitle,
          x: percent(-30),
          rotation: -90,
          y: percent(50),
          centerX: p50,
        })
      );
    }

    this.xAxis.data.setAll(this.data);
    this.yAxis.data.setAll(this.data);
  }

  mapData(data: ChartDataRecord[]): ChartDataRecord[] {
    const preparedData: ChartDataRecord[] = cloneDeep(data);

    const mappedData = mapDataDumbbell(preparedData, this, true);
    return this.showItems(mappedData, this.settings.decompositionMetric, `${this.settings.displayMetric}_max`, "first");
  }

  protected createCursor() {
    const cursor = (this.chart as XYChart).set(
      "cursor",
      XYCursor.new(this.root, {
        xAxis: this.xAxis,
        yAxis: this.yAxis,
      })
    );
    cursor.lineX.set("visible", false);
    cursor.lineY.set("visible", true);
  }

  private formatMetricValueForDisplay = (key: string, value: unknown): unknown => {
    const metric = this.settings.displayMetric;
    const isMetricField =
      key === metric ||
      key === `${metric}_min` ||
      key === `${metric}_max` ||
      key === `${metric}_median` ||
      key === `${metric}_25th` ||
      key === `${metric}_75th`;

    if (!isMetricField) return value;
    if (typeof value !== "number" || !Number.isFinite(value)) return value;

    const pattern = this.getCompactAxesMetricFormatter(metric, this.data);
    return this.root.numberFormatter.format(value, pattern);
  };

  protected chartDataFields() {
    const getComputedFields = (metric: string) => [`${metric}_max`, `${metric}_min`, `${metric}_median`];
    const displaySettingsNames = ["displayMetric", "decompositionMetric"];
    const existingSettings = Object.keys(this?.settings || {}).filter((key) => displaySettingsNames.includes(key));

    return [
      ...existingSettings.map((settingName) => this.settings[settingName]),
      ...getComputedFields(this.settings.displayMetric),
    ];
  }
}
